import React from 'react';
import { ArrowRight } from 'lucide-react';
import BackgroundAnimation from '../../components/BackgroundAnimation';

const SocialMedia = () => {
  const platforms = [
    { name: 'Instagram', desc: 'Visual storytelling and influencer partnerships' },
    { name: 'Facebook', desc: 'Community building and targeted advertising' },
    { name: 'Twitter', desc: 'Real-time engagement and thought leadership' },
    { name: 'YouTube', desc: 'Video content and long-form storytelling' }
  ];

  const strategies = [
    { title: 'Content Strategy', desc: 'Data-driven content planning that resonates', items: ['Content calendar', 'Trend analysis', 'Brand voice development'] },
    { title: 'Community Management', desc: 'Building engaged communities', items: ['24/7 monitoring', 'Response management', 'Community guidelines'] },
    { title: 'Paid Advertising', desc: 'Strategic campaigns that maximize ROI', items: ['Target audience research', 'Ad creative design', 'Performance optimization'] },
    { title: 'Analytics & Reporting', desc: 'Comprehensive insights to optimize', items: ['Performance metrics', 'ROI tracking', 'Monthly reports'] }
  ];

  const results = [
    { metric: '300%', label: 'Average Follower Growth' },
    { metric: '85%', label: 'Engagement Rate Increase' },
    { metric: '2.5x', label: 'Lead Generation' },
    { metric: '60%', label: 'Cost Per Acquisition Reduction' }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      
      <section className="py-24" style={{ padding: '120px 7.6923% 80px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Social Media
            <span className="block" style={{ color: 'var(--brand-primary)' }}>Growth</span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Grow Your Social Presence Fast</p>
          <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            Build engaged communities, increase brand awareness, and drive conversions with our data-driven social media strategies across all major platforms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
              <span>Start Growing Today</span>
              <ArrowRight size={20} />
            </a>
            <a href="/services" className="button-text dark-button-animate" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>View All Services</a>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>We Master Every Platform</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {platforms.map((platform, idx) => (
              <div key={idx} className="p-6 rounded-lg text-center" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--brand-primary)' }}>{platform.name}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{platform.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Our Growth Strategies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {strategies.map((strategy, idx) => (
              <div key={idx} className="p-8 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <h3 className="heading-2 mb-2" style={{ color: 'var(--text-primary)' }}>{strategy.title}</h3>
                <p className="body-medium mb-4" style={{ color: 'var(--text-secondary)' }}>{strategy.desc}</p>
                <ul className="space-y-2">
                  {strategy.items.map((item, i) => (
                    <li key={i} className="body-small flex items-center" style={{ color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--brand-primary)', marginRight: '8px' }}>•</span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Proven Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {results.map((result, idx) => (
              <div key={idx} className="text-center p-6 rounded-lg" style={{ background: 'rgba(0, 255, 209, 0.05)', border: '1px solid var(--brand-primary)' }}>
                <div className="display-large mb-2" style={{ color: 'var(--brand-primary)' }}>{result.metric}</div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{result.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Grow Your Social Media?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Let's create a strategy that drives real engagement and growth</p>
          <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
            <span>Start Growing</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default SocialMedia;